import React, { useState, useMemo, useCallback } from "react";
import { useProcurementUser } from "../context/ProcurementUserContext";
import ProcurementUserSwitcher from "../components/ProcurementUserSwitcher";

const API = process.env.REACT_APP_API_URL || "";

const C = {
  bg: "#0F1117", card: "#161C2E", cardBorder: "#222A40",
  gold: "#C9972B", goldDim: "#7A5A18",
  text: "#F4F0E8", textSec: "#9EA8C0", textDim: "#5C657A",
  red: "#E24B4A", green: "#1D9E75", amber: "#BA7517",
  rule: "#2A3048", blue: "#3B82F6",
};

const fmtCur = (v) => v != null ? `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—";
const PAGE_SIZE = 100;

function Toast({ msg, onClose }) {
  if (!msg) return null;
  return (
    <div style={{ position: "fixed", bottom: 24, right: 24, background: C.green, color: "#fff", padding: "10px 20px", borderRadius: 8, fontSize: 13, fontFamily: "monospace", zIndex: 999, boxShadow: "0 4px 20px rgba(0,0,0,0.4)" }}>
      {msg}
      <button onClick={onClose} style={{ marginLeft: 12, background: "none", border: "none", color: "#fff", cursor: "pointer", fontWeight: 700 }}>x</button>
    </div>
  );
}

export default function ProjectFinancials() {
  const { activeUser } = useProcurementUser();
  const hdrs = { "Content-Type": "application/json", "X-User": activeUser.identifier };

  const [allocation, setAllocation] = useState("");
  const [financials, setFinancials] = useState(null);
  const [subtotals, setSubtotals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);

  const [jeResults, setJeResults] = useState(null);
  const [jeLoading, setJeLoading] = useState(false);

  const [exposureRow, setExposureRow] = useState(null);
  const [expAmount, setExpAmount] = useState("");
  const [expNotes, setExpNotes] = useState("");
  const [expErr, setExpErr] = useState(null);

  const [filterPfr, setFilterPfr] = useState("");
  const [filterVendor, setFilterVendor] = useState("");
  const [sortCol, setSortCol] = useState("pfr_code");
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(0);
  const [toast, setToast] = useState(null);

  const loadFinancials = useCallback(async () => {
    if (!allocation.trim()) return;
    setLoading(true); setError(null); setAccessDenied(false); setFinancials(null); setSubtotals(null); setJeResults(null); setPage(0);
    try {
      const r = await fetch(`${API}/procurement/financials/${encodeURIComponent(allocation.trim())}`, { headers: hdrs });
      if (r.status === 403) { setAccessDenied(true); setLoading(false); return; }
      const d = await r.json();
      setFinancials(d.financials || []);
      setSubtotals(d.subtotals || {});
    } catch (e) { setError(e.message); }
    setLoading(false);
  }, [allocation]);

  const runJeAnalysis = async () => {
    setJeLoading(true);
    try {
      const r = await fetch(`${API}/procurement/chat`, { method: "POST", headers: hdrs, body: JSON.stringify({ tool: "find_journal_entries", tool_params: { allocation: allocation.trim() }, message: "" }) });
      const d = await r.json();
      setJeResults(d.result || d);
    } catch (e) { setJeResults({ error: e.message }); }
    setJeLoading(false);
  };

  const confirmJe = async (financialId, targetProject) => {
    await fetch(`${API}/procurement/financials/${financialId}/je-confirm`, { method: "POST", headers: hdrs, body: JSON.stringify({ je_target_project: targetProject }) });
    setToast("JE Confirmed"); loadFinancials();
    setTimeout(() => setToast(null), 3000);
  };

  const submitExposure = async () => {
    if (!expAmount || !expNotes.trim()) { setExpErr("Amount and notes required"); return; }
    setExpErr(null);
    try {
      await fetch(`${API}/procurement/financials/exposure`, { method: "POST", headers: hdrs, body: JSON.stringify({ allocation: exposureRow.allocation || allocation.trim(), po_number: exposureRow.po_number, line_number: exposureRow.line_number, exposure_amount: parseFloat(expAmount), exposure_notes: expNotes }) });
      setExposureRow(null); setExpAmount(""); setExpNotes(""); setToast("Exposure added"); loadFinancials();
      setTimeout(() => setToast(null), 3000);
    } catch (e) { setExpErr(e.message); }
  };

  const exportCsv = () => {
    if (!financials?.length) return;
    const cols = ["allocation","build_phase_seq","equipment_type","vendor_name","po_number","line_number","pfr_code","item_description","quantity","unit_price","line_amount","exposure_amount","notes","je_required","je_completed"];
    const csv = [cols.join(","), ...financials.map(r => cols.map(c => `"${(r[c] ?? "").toString().replace(/"/g, '""')}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `financials_${allocation}_${new Date().toISOString().slice(0,10)}.csv`; a.click();
  };

  const handleSort = (col) => { if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc"); else { setSortCol(col); setSortDir("asc"); } setPage(0); };

  // Derived data
  const pfrCodes = useMemo(() => [...new Set((financials || []).map(r => r.pfr_code).filter(Boolean))].sort(), [financials]);
  const vendorNames = useMemo(() => [...new Set((financials || []).map(r => r.vendor_name).filter(Boolean))].sort(), [financials]);

  const filteredRows = useMemo(() => {
    let rows = financials || [];
    if (filterPfr) rows = rows.filter(r => r.pfr_code === filterPfr);
    if (filterVendor) rows = rows.filter(r => r.vendor_name === filterVendor);
    return rows;
  }, [financials, filterPfr, filterVendor]);

  const sortedRows = useMemo(() => {
    return [...filteredRows].sort((a, b) => {
      let av = a[sortCol], bv = b[sortCol];
      if (av == null) return 1; if (bv == null) return -1;
      if (typeof av === "string" && typeof bv === "string") return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === "asc" ? (av - bv) : (bv - av);
    });
  }, [filteredRows, sortCol, sortDir]);

  const pagedRows = useMemo(() => sortedRows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE), [sortedRows, page]);
  const totalPages = Math.ceil(sortedRows.length / PAGE_SIZE);

  const pfrGroups = useMemo(() => {
    const g = {};
    for (const r of sortedRows) { const k = r.pfr_code || "UNASSIGNED"; if (!g[k]) g[k] = []; g[k].push(r); }
    return g;
  }, [sortedRows]);

  // 403 full-page block
  if (accessDenied) {
    return (
      <div style={{ background: C.bg, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: C.text }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>403</div>
          <div style={{ fontSize: 18, color: C.amber }}>Access restricted — internal users only</div>
        </div>
      </div>
    );
  }

  const jePending = subtotals?.je_pending_count || 0;
  const si = (col) => sortCol === col ? (sortDir === "asc" ? " \u2191" : " \u2193") : " \u2195";
  const th = { padding: "8px 10px", fontSize: 11, fontFamily: "monospace", textTransform: "uppercase", color: C.textDim, cursor: "pointer", whiteSpace: "nowrap", borderBottom: `1px solid ${C.rule}`, textAlign: "left", letterSpacing: "0.06em" };
  const td = { padding: "6px 10px", fontSize: 12, borderBottom: `1px solid ${C.rule}`, fontFamily: "monospace" };
  const tdR = { ...td, textAlign: "right" };

  return (
    <div style={{ background: C.bg, minHeight: "100vh", color: C.text, fontFamily: "'Outfit', system-ui, sans-serif" }}>
      <div style={{ maxWidth: 1440, margin: "0 auto", padding: "16px 24px 48px" }}>

        {/* HEADER */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, borderBottom: `1px solid ${C.rule}`, paddingBottom: 12 }}>
          <div>
            <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>Project Financials</h1>
            <div style={{ fontSize: 11, color: C.amber, fontStyle: "italic" }}>Internal — not accessible to external users</div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <ProcurementUserSwitcher />
            <button onClick={loadFinancials} style={{ background: C.card, border: `1px solid ${C.cardBorder}`, color: C.textSec, borderRadius: 8, padding: "6px 14px", fontSize: 12, cursor: "pointer", fontFamily: "monospace" }}>Refresh</button>
            <button onClick={exportCsv} disabled={!financials?.length} style={{ background: C.gold, color: C.bg, border: "none", borderRadius: 8, padding: "6px 14px", fontSize: 12, cursor: "pointer", fontFamily: "monospace", opacity: financials?.length ? 1 : 0.4 }}>Export CSV</button>
          </div>
        </div>

        {/* PROJECT SELECTOR */}
        <div style={{ display: "flex", gap: 8, marginBottom: 16, alignItems: "center" }}>
          <input value={allocation} onChange={e => setAllocation(e.target.value)} placeholder="Enter allocation (e.g. DEC3.0-PHX-B1)" onKeyDown={e => e.key === "Enter" && loadFinancials()}
            style={{ background: C.card, border: `1px solid ${C.cardBorder}`, color: C.text, borderRadius: 8, padding: "8px 14px", fontSize: 13, fontFamily: "monospace", flex: 1, maxWidth: 400, outline: "none" }} />
          <button onClick={loadFinancials} disabled={loading || !allocation.trim()} style={{ background: C.gold, color: C.bg, border: "none", borderRadius: 8, padding: "8px 20px", fontWeight: 600, fontSize: 12, cursor: "pointer", fontFamily: "monospace" }}>
            {loading ? "Loading..." : "Load"}
          </button>
        </div>

        {error && <div style={{ color: C.red, marginBottom: 12, fontSize: 13 }}>{error}</div>}

        {/* NOT LOADED */}
        {!financials && !loading && !error && (
          <div style={{ textAlign: "center", padding: "80px 0", color: C.textDim }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>&#x1F4CA;</div>
            <div style={{ fontSize: 15 }}>Enter a project allocation above to load financials.</div>
          </div>
        )}

        {/* LOADED BUT EMPTY */}
        {financials && financials.length === 0 && (
          <div style={{ textAlign: "center", padding: "60px 0", color: C.textDim }}>
            <div style={{ fontSize: 15 }}>No financial records found for <strong style={{ color: C.gold }}>{allocation}</strong>.</div>
            <div style={{ fontSize: 12, marginTop: 6 }}>Run Oracle ingest to populate PO line data.</div>
          </div>
        )}

        {/* SUMMARY CARDS */}
        {subtotals && financials && financials.length > 0 && (
          <>
            <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
              {[
                { label: "PO Committed", value: fmtCur(subtotals.po_committed), color: C.green },
                { label: "Exposures", value: fmtCur(subtotals.exposures), color: C.amber },
                { label: "Combined Total", value: fmtCur(subtotals.combined), color: C.text },
                { label: "JE Pending", value: jePending, color: jePending > 0 ? C.red : C.green },
              ].map((s, i) => (
                <div key={i} style={{ flex: 1, minWidth: 180, background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: "16px 20px" }}>
                  <div style={{ fontSize: 10, fontFamily: "monospace", textTransform: "uppercase", letterSpacing: "0.12em", color: C.textDim, marginBottom: 4 }}>{s.label}</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: s.color, fontFamily: "monospace" }}>{s.value}</div>
                </div>
              ))}
            </div>

            {/* JE PANEL */}
            {jePending > 0 && (
              <div style={{ background: C.card, border: `1px solid ${C.amber}44`, borderLeft: `3px solid ${C.amber}`, borderRadius: 12, padding: "20px 24px", marginBottom: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.amber, marginBottom: 6, fontFamily: "monospace" }}>JOURNAL ENTRIES REQUIRED</div>
                <div style={{ fontSize: 13, color: C.textSec, marginBottom: 12 }}>The AI has identified {jePending} PO line{jePending > 1 ? "s" : ""} where costs need to move between projects due to reallocation.</div>
                <button onClick={runJeAnalysis} disabled={jeLoading} style={{ background: C.amber, color: C.bg, border: "none", borderRadius: 8, padding: "6px 16px", fontWeight: 600, fontSize: 12, cursor: "pointer", fontFamily: "monospace", marginBottom: 10 }}>
                  {jeLoading ? "Analyzing..." : "Run JE Analysis"}
                </button>
                {jeResults && !jeResults.error && (jeResults.entries || []).map((e, i) => (
                  <div key={i} style={{ marginTop: 8, padding: 12, background: `${C.red}0a`, border: `1px solid ${C.red}22`, borderRadius: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontFamily: "monospace", fontSize: 12, color: C.amber }}>{e.po_number}-{e.line_number} | {fmtCur(e.amount)}</span>
                      <button onClick={() => confirmJe(e.financial_id, e.to_project)} style={{ background: C.green, color: "#fff", border: "none", borderRadius: 6, padding: "4px 12px", fontSize: 11, cursor: "pointer", fontFamily: "monospace" }}>Confirm JE Complete</button>
                    </div>
                    <div style={{ fontSize: 11, color: C.textSec, marginTop: 4 }}>{e.from_project} &rarr; {e.to_project}</div>
                    <div style={{ fontSize: 12, color: C.text, marginTop: 4, fontStyle: "italic", paddingLeft: 12 }}>{e.narrative}</div>
                  </div>
                ))}
              </div>
            )}

            {/* FILTERS */}
            <div style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: "center" }}>
              <select value={filterPfr} onChange={e => { setFilterPfr(e.target.value); setPage(0); }} style={{ background: C.card, border: `1px solid ${C.cardBorder}`, color: C.textSec, borderRadius: 6, padding: "4px 8px", fontSize: 12, fontFamily: "monospace" }}>
                <option value="">All PFR Codes</option>
                {pfrCodes.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              <select value={filterVendor} onChange={e => { setFilterVendor(e.target.value); setPage(0); }} style={{ background: C.card, border: `1px solid ${C.cardBorder}`, color: C.textSec, borderRadius: 6, padding: "4px 8px", fontSize: 12, fontFamily: "monospace" }}>
                <option value="">All Vendors</option>
                {vendorNames.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
              <span style={{ fontSize: 11, color: C.textDim, fontFamily: "monospace" }}>{sortedRows.length} rows | Page {page + 1}/{totalPages || 1}</span>
            </div>

            {/* TABLE */}
            <div style={{ overflowX: "auto", borderRadius: 12, border: `1px solid ${C.cardBorder}` }}>
              <table style={{ width: "100%", borderCollapse: "collapse", background: C.card }}>
                <thead>
                  <tr>
                    <th style={th}>Type</th>
                    <th style={th}>JE</th>
                    <th style={th} onClick={() => handleSort("allocation")}>Alloc{si("allocation")}</th>
                    <th style={th} onClick={() => handleSort("build_phase_seq")}>Phase{si("build_phase_seq")}</th>
                    <th style={th} onClick={() => handleSort("equipment_type")}>Equip{si("equipment_type")}</th>
                    <th style={th} onClick={() => handleSort("vendor_name")}>Vendor{si("vendor_name")}</th>
                    <th style={th} onClick={() => handleSort("po_number")}>PO #{si("po_number")}</th>
                    <th style={th}>Line</th>
                    <th style={th} onClick={() => handleSort("pfr_code")}>PFR{si("pfr_code")}</th>
                    <th style={th}>Description</th>
                    <th style={{ ...th, textAlign: "right" }}>Qty</th>
                    <th style={{ ...th, textAlign: "right" }}>Unit Price</th>
                    <th style={{ ...th, textAlign: "right" }}>Line Amt</th>
                    <th style={{ ...th, textAlign: "right" }}>Exposure</th>
                    <th style={th}>Notes</th>
                    <th style={th}></th>
                  </tr>
                </thead>
                <tbody>
                  {pagedRows.map((r, i) => {
                    const isExp = r.exposure_amount != null && r.exposure_amount > 0;
                    const jeReq = r.je_required && !r.je_completed;
                    const jeDone = r.je_completed;
                    const bl = jeReq ? `3px solid ${C.red}` : jeDone ? `3px solid ${C.green}` : isExp ? `3px solid ${C.amber}44` : "3px solid transparent";
                    const bg = jeReq ? `${C.red}08` : isExp ? `${C.amber}06` : "transparent";
                    return (
                      <tr key={r.id || i} style={{ borderLeft: bl, background: bg }}>
                        <td style={td}><span style={{ display: "inline-block", padding: "1px 6px", borderRadius: 4, fontSize: 10, fontWeight: 700, background: isExp ? `${C.amber}22` : `${C.blue}22`, color: isExp ? C.amber : C.blue }}>{isExp ? "EXP" : "PO"}</span></td>
                        <td style={td}>{jeReq ? <span style={{ color: C.red, fontWeight: 700, fontSize: 10 }}>JE</span> : jeDone ? <span style={{ color: C.green }}>&#10003;</span> : ""}</td>
                        <td style={td}>{r.allocation}</td>
                        <td style={td}>{r.build_phase_seq}</td>
                        <td style={td}>{r.equipment_type}</td>
                        <td style={{ ...td, color: C.textSec }}>{r.vendor_name}</td>
                        <td style={td}>{r.po_number}</td>
                        <td style={td}>{r.line_number}</td>
                        <td style={{ ...td, color: C.amber }}>{r.pfr_code}</td>
                        <td style={{ ...td, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.item_description}>{r.item_description?.slice(0, 40)}</td>
                        <td style={tdR}>{r.quantity}</td>
                        <td style={tdR}>{fmtCur(r.unit_price)}</td>
                        <td style={{ ...tdR, fontWeight: 700 }}>{fmtCur(r.line_amount)}</td>
                        <td style={{ ...tdR, color: C.amber }}>{r.exposure_amount ? fmtCur(r.exposure_amount) : "—"}</td>
                        <td style={{ ...td, maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.notes}>{r.notes?.slice(0, 20) || ""}</td>
                        <td style={td}><button onClick={() => { setExposureRow(r); setExpAmount(""); setExpNotes(""); setExpErr(null); }} style={{ background: "none", border: `1px solid ${C.textDim}`, color: C.textSec, borderRadius: 4, padding: "2px 8px", fontSize: 10, cursor: "pointer", fontFamily: "monospace" }}>+ Exp</button></td>
                      </tr>
                    );
                  })}
                  {/* PFR Subtotals */}
                  {!filterPfr && Object.entries(pfrGroups).map(([pfr, rows]) => (
                    <tr key={`sub-${pfr}`} style={{ background: `${C.rule}44` }}>
                      <td colSpan={12} style={{ ...td, fontWeight: 700, color: C.textSec }}>PFR: {pfr} Subtotal</td>
                      <td style={{ ...tdR, fontWeight: 700 }}>{fmtCur(rows.reduce((s, r) => s + (r.line_amount || 0), 0))}</td>
                      <td style={{ ...tdR, fontWeight: 700, color: C.amber }}>{fmtCur(rows.reduce((s, r) => s + (r.exposure_amount || 0), 0))}</td>
                      <td colSpan={2} style={td}></td>
                    </tr>
                  ))}
                  {/* Grand Total */}
                  <tr style={{ background: `${C.rule}88` }}>
                    <td colSpan={12} style={{ ...td, fontWeight: 700, fontSize: 13, color: C.text }}>TOTAL</td>
                    <td style={{ ...tdR, fontWeight: 700, fontSize: 13 }}>{fmtCur(subtotals.po_committed)}</td>
                    <td style={{ ...tdR, fontWeight: 700, fontSize: 13, color: C.amber }}>{fmtCur(subtotals.exposures)}</td>
                    <td colSpan={2} style={td}></td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* PAGINATION */}
            {totalPages > 1 && (
              <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 12 }}>
                <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} style={{ background: C.card, border: `1px solid ${C.cardBorder}`, color: C.textSec, borderRadius: 6, padding: "4px 12px", fontSize: 12, cursor: "pointer" }}>&laquo; Prev</button>
                <span style={{ color: C.textDim, fontSize: 12, padding: "4px 8px", fontFamily: "monospace" }}>{page + 1} / {totalPages}</span>
                <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} style={{ background: C.card, border: `1px solid ${C.cardBorder}`, color: C.textSec, borderRadius: 6, padding: "4px 12px", fontSize: 12, cursor: "pointer" }}>Next &raquo;</button>
              </div>
            )}
          </>
        )}
      </div>

      {/* EXPOSURE MODAL */}
      {exposureRow && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 900 }} onClick={() => setExposureRow(null)}>
          <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 16, padding: 28, width: 420, maxWidth: "90vw" }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Add Exposure</div>
            <div style={{ fontSize: 12, color: C.textSec, marginBottom: 16, fontFamily: "monospace" }}>{exposureRow.po_number} Line {exposureRow.line_number} | {exposureRow.item_description?.slice(0, 50)}</div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 4 }}>Exposure Amount ($)</label>
              <input type="number" value={expAmount} onChange={e => setExpAmount(e.target.value)} style={{ width: "100%", background: C.bg, border: `1px solid ${C.cardBorder}`, color: C.text, borderRadius: 8, padding: "8px 12px", fontSize: 14, fontFamily: "monospace", outline: "none", boxSizing: "border-box" }} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 4 }}>Notes</label>
              <textarea value={expNotes} onChange={e => setExpNotes(e.target.value)} rows={3} style={{ width: "100%", background: C.bg, border: `1px solid ${C.cardBorder}`, color: C.text, borderRadius: 8, padding: "8px 12px", fontSize: 13, fontFamily: "monospace", outline: "none", resize: "vertical", boxSizing: "border-box" }} />
            </div>
            {expErr && <div style={{ color: C.red, fontSize: 12, marginBottom: 8 }}>{expErr}</div>}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setExposureRow(null)} style={{ background: "none", border: `1px solid ${C.textDim}`, color: C.textSec, borderRadius: 8, padding: "6px 16px", fontSize: 12, cursor: "pointer" }}>Cancel</button>
              <button onClick={submitExposure} style={{ background: C.gold, color: C.bg, border: "none", borderRadius: 8, padding: "6px 20px", fontWeight: 600, fontSize: 12, cursor: "pointer", fontFamily: "monospace" }}>Add Exposure</button>
            </div>
          </div>
        </div>
      )}

      <Toast msg={toast} onClose={() => setToast(null)} />
    </div>
  );
}
